import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './components/ThemeSelection.css';
import NavbarPanel from './components/NavbarPanel';
import ViewSelection from './components/ViewSelection';
import ProductView from './components/ProductView';
import Sidebar from './components/Sidebar';
import ThemeSelection from './components/ThemeSelection';
import { BiArrowBack } from 'react-icons/bi';
import HeaderBar from './components/HeaderBar';
import PreviewPage from './components/PreviewPage';
import JsonDesigner from './components/JsonDesigner';
import { getUrlParams, getProductConfigUrl, getThemeIndexFromUrl, getOptionSelectionsFromUrl, isKioskMode } from './utils/urlUtils';
import { useTranslation } from './utils/languageUtils';
import { generateProductCombinations, filterViewsByLinkedView, getOptionDisplayName, resolveOptionKeyByName } from './utils/productOptionsUtils';
import { applySiteStylesToDocument } from './config/siteStylesConfig';
import { clearLocalStorageData, getLocalStorageInfo } from './utils/dataUtils';
import { setGlobalSelectedOptions } from './utils/globalOptions';
import './utils/kioskModeHandler.js';
import headerIcon from './assets/Header.png';

// Helper function to ensure view is always an object
const ensureViewObject = (view) => {
  if (!view) return { id: 'default-view', name: { en: 'Default View', cn: '默认视图' } };
  if (typeof view === 'string') {
    return { id: view, name: { en: view, cn: view } };
  }
  if (typeof view === 'object' && view.id) {
    return view; // Already an object
  }
  // Fallback for any other format
  return { id: String(view), name: { en: String(view), cn: String(view) } };
};

// Default empty products array
const defaultProducts = [
  {
    id: 'default-product',
    name: 'Default Product',
    views: [{ id: 'default-view', name: { en: 'Default View', cn: '默认视图' } }],
    // colors removed as per requirement
  }
];

function App() {
  const { t } = useTranslation();

  // Kiosk mode state
  const [isKiosk, setIsKiosk] = useState(false);

  // In kiosk mode we sometimes need to hide ThemeSelection even when no theme is selected
  // (e.g., returning to attract/cover overlay). This flag suppresses the auto-show logic.
  const [kioskThemeSelectionHidden, setKioskThemeSelectionHidden] = useState(false);

  // Modal state for "clear saved designs" prompt
  const [showClearDesignModal, setShowClearDesignModal] = useState(false);

  // Initialize kiosk mode
  useEffect(() => {
    setIsKiosk(isKioskMode());
  }, []);

  // Set kiosk mode attribute on body and CSS variables
  useEffect(() => {
    if (isKiosk) {
      document.body.setAttribute('data-kiosk', 'true');
      // Set CSS variable for Header.png background
      document.documentElement.style.setProperty('--kiosk-header-bg', `url(${headerIcon})`);
    } else {
      document.body.removeAttribute('data-kiosk');
      document.documentElement.style.removeProperty('--kiosk-header-bg');
    }
  }, [isKiosk, headerIcon]);

  // Decide whether to show the clear-design modal on first app mount (only in non-kiosk mode)
  useEffect(() => {
    if (isKiosk) return; // Skip in kiosk mode
    try {
      const info = getLocalStorageInfo();
      // Only show modal if we actually detect old design-related data
      if (info && Array.isArray(info.designKeys) && info.designKeys.length > 0) {
        setShowClearDesignModal(true);
      }
    } catch (e) {
      console.error('Design clear prompt init failed:', e);
    }
  }, [isKiosk]);

  const handleClearDesignsConfirm = () => {
    try {
      const result = clearLocalStorageData();
      if (!result.success) {
        console.error('Failed to clear localStorage:', result.error);
      }
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    } finally {
      setShowClearDesignModal(false);
    }
  };

  const handleKeepDesigns = () => {
    setShowClearDesignModal(false);
  };

  const [activeTab, setActiveTab] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Track device size to alter default tab behavior (mobile defaults to Toolbox, desktop doesn't)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    // Initialize CSS variables from active SiteStyle
    applySiteStylesToDocument();
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);



  const [baseUrl, setBaseUrl] = useState('');
  const [designUrl, setDesignUrl] = useState('');
  const [products, setProducts] = useState(defaultProducts);
  const [selectedProduct, setSelectedProduct] = useState(defaultProducts[0]);
  
  // Set kiosk step to designer when showing ProductView
  useEffect(() => {
    if (isKiosk && selectedProduct && window.kioskHandler) {
      window.kioskHandler.setKioskStep('designer');
    }
  }, [isKiosk, selectedProduct]);
  
  // Handle both uppercase and lowercase property names in initial state
  const initialViews = defaultProducts[0]?.views || defaultProducts[0]?.Views || [{ id: 'default-view', name: { en: 'Default View', cn: '默认视图' } }];
  
  const [selectedView, setSelectedView] = useState(ensureViewObject(initialViews[0]));
  
  // Custom handler for view selection that uses KonvaController's setSelectedView method
  // Color selection removed as per requirement
  const [konvaController, setKonvaController] = useState(null);
  // Config data must be declared before any hooks/handlers that read it (e.g., handleViewSelect)
  const [configData, setConfigData] = useState(null); // Store product configuration data

  // Keep latest values for event handlers registered once
  const selectedProductRef = React.useRef(selectedProduct);
  const configDataRef = React.useRef(configData);
  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);
  useEffect(() => {
    configDataRef.current = configData;
  }, [configData]);
  
  const handleViewSelect = useCallback((view) => {
    try {
      // Prevent selecting views that are disabled by linked_view mapping
      const optionsCfg = (configData && configData.SupportedModels && configData.SupportedModels.Options) || {};
      const productViews = (selectedProduct?.views || selectedProduct?.Views || []);
      const selectedOptions = (window.selectedOptions) || {};
      const filtered = filterViewsByLinkedView(optionsCfg, selectedOptions, productViews);
      const getId = (v) => (typeof v === 'string' ? v : (v && v.id) ? v.id : String(v));
      const targetId = getId(view);
      const isAllowed = filtered.some(v => getId(v) === targetId);
      const nextView = isAllowed ? view : (filtered[0] || view);
      if (!isAllowed) {
        console.warn('[App] Attempted to select disabled view:', targetId, '-> switching to', getId(nextView));
      }
      setSelectedView(ensureViewObject(nextView));
      if (konvaController?.setSelectedView) {
        konvaController.setSelectedView(ensureViewObject(nextView));
      }
      const event = new CustomEvent('viewChanged', { detail: { view: ensureViewObject(nextView) } });
      window.dispatchEvent(event);
    } catch (e) {
      console.warn('[App] handleViewSelect fallback due to error:', e);
      setSelectedView(ensureViewObject(view));
      if (konvaController?.setSelectedView) {
        konvaController.setSelectedView(ensureViewObject(view));
      }
      const event = new CustomEvent('viewChanged', { detail: { view: ensureViewObject(view) } });
      window.dispatchEvent(event);
    }
  }, [konvaController, configData, selectedProduct]);
  
  // Sync only the React state and broadcast event (do NOT call controller.setSelectedView).
  // Useful when the controller has already switched/restored the view (e.g., after export)
  const syncSelectedViewOnly = useCallback((view) => {
    setSelectedView(view);
    try {
      const event = new CustomEvent('viewChanged', { detail: { view } });
      window.dispatchEvent(event);
    } catch {}
  }, []);
  
  // Debug: Log selectedView changes
  useEffect(() => {
    
  }, [selectedView]);
  const [hasStickerSet, setHasStickerSet] = useState(false); // Track if Themes exist and have stickers
  
  // Theme selection states
  const [showThemeSelection, setShowThemeSelection] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);

  // Kiosk flow: Theme -> Product -> Designer
  const [showProductSelection, setShowProductSelection] = useState(false);

  // Kiosk product selection internal state (2-level selection)
  const [kioskModelGroup, setKioskModelGroup] = useState(null);

  const getKioskSeriesIdFromModelValue = useCallback((modelValue) => {
    const raw = String(modelValue || '').trim();
    if (!raw) return null;
    const ipMatch = raw.match(/^IP\s*(\d{2})/i);
    if (ipMatch) return `IPHONE_${ipMatch[1]}`;
    const sMatch = raw.match(/^S\s*(\d{2})/i);
    if (sMatch) return `S_${sMatch[1]}`;
    return raw;
  }, []);

  // Reset group selection whenever we leave the product selection screen
  useEffect(() => {
    if (!showProductSelection) {
      setKioskModelGroup(null);
    }
  }, [showProductSelection]);
  
  const canvasContainerRef = React.useRef(null);
  
  // Let Toolbox's Layer button open the ProductView's local Layer modal via imperative handle
  const handleOpenLayerModalFromToolbox = useCallback(() => {
    try {
      const api = canvasContainerRef.current;
      if (api && typeof api.openLayerModal === 'function') {
        api.openLayerModal();
      } else {
        console.warn('[App] ProductView ref not ready: openLayerModal missing');
      }
    } catch (e) {
      console.warn('[App] Failed to open layer modal via ref:', e);
    }
  }, []);

  const handleShowLayerModal = useCallback(() => {
    try {
      const api = canvasContainerRef.current;
      if (api && typeof api.openLayerModal === 'function') {
        api.openLayerModal();
      }
    } catch (e) {
      console.error('Error opening layer modal:', e);
    }
  }, []);

  
  // Define controller ready handler with useCallback
  const handleControllerReady = useCallback((controller) => {
    
    setKonvaController(controller);
  }, []);
  
  // Theme selection handlers
  const handleShowThemeSelection = useCallback((show) => {
    
    
    
    setShowThemeSelection(show);
  }, [configData]);
  
  const handleThemeSelect = useCallback((theme) => {
    
    setSelectedTheme(theme);
    setShowThemeSelection(false);
    if (window.kioskHandler) {
      setShowProductSelection(true);
      window.kioskHandler.setKioskStep('product-selection');
    }
  }, []);
  
  const handleStickerSelect = useCallback((sticker, theme) => {
    
    // Set the selected theme and the specific sticker set
    setSelectedTheme({
      ...theme,
      selectedSticker: sticker
    });
    setShowThemeSelection(false);
    if (window.kioskHandler) {
      setShowProductSelection(true);
      window.kioskHandler.setKioskStep('product-selection');
    }
  }, []);
  
  const handleThemeSelectionClose = useCallback(() => {
    
    setShowThemeSelection(false);
  }, []);

  // Handle kiosk back button
  const handleKioskBack = useCallback(() => {
    if (window.kioskHandler) {
      window.kioskHandler.handleKioskBack();
    }
  }, []);
  
  // Clean up KonvaController on unmount
  useEffect(() => {
    return () => {
      if (konvaController?.destroy) {
        
        konvaController.destroy();
      }
    };
  }, [konvaController]);

  // Update page title based on language
  useEffect(() => {
    document.title = t('app.title', 'Product Customizer');
  }, [t]);

  // Detect test mode from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');
    setIsTestMode(testParam === 'true');
    
  }, []);

  // Log when configData changes
  useEffect(() => {
    
  }, [configData, selectedTheme]);

  // Load products data from online config JSON
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Get product ID from URL query parameter using the utility function
        const { id: productId } = getUrlParams();
        
        
        
        // Use the utility function to get the product config URL with timestamp
        // Get user from URL params to use in config URL
        const { user: urlUser } = getUrlParams();
        const configUrl = getProductConfigUrl(productId, urlUser);
        const response = await fetch(configUrl);
        if (!response.ok) {
          throw new Error(`Failed to load products data for ID: ${productId}`);
        }
        const configData = await response.json();
        
        // Add a fallback FontUrl if none is provided
        if (!configData.FontUrl) {
          console.warn('No FontUrl provided in config, using default');
          configData.FontUrl = '';
        } else {
          // Add timestamp to FontUrl to prevent caching
          const timestamp = new Date().getTime();
          const separator = configData.FontUrl.includes('?') ? '&' : '?';
          configData.FontUrl = `${configData.FontUrl}${separator}t=${timestamp}`;
          
        }
        
        // Store the config data to share with other components
        setConfigData(configData);
        
        // Update hasStickerSet based on new Themes structure
        const hasThemes = configData.Themes && configData.Themes.length > 0;
        
        setHasStickerSet(hasThemes);

        // If URL has a valid theme index, auto-select that theme and skip Theme Selection
        try {
          const idx = getThemeIndexFromUrl();
          if (idx !== null && Array.isArray(configData.Themes)) {
            if (idx >= 0 && idx < configData.Themes.length) {
              setSelectedTheme(configData.Themes[idx]);
            } else {
              console.warn('[App] themeIndex out of range:', idx, 'available:', configData.Themes.length);
            }
          }
        } catch (e) {
          console.warn('[App] Failed to apply themeIndex from URL:', e);
        }
        
        // Process SupportedModels - handle both old array format and new Options structure
        let productsList = [];
        
        if (configData.SupportedModels) {
          if (Array.isArray(configData.SupportedModels)) {
            // Old format - array of products
            
            productsList = configData.SupportedModels;
          } else if (typeof configData.SupportedModels === 'object') {
            // New format - hierarchical Options structure
            
            productsList = generateProductCombinations(configData.SupportedModels);
            
          } else {
            throw new Error('Invalid SupportedModels format');
          }
        } else {
          throw new Error('SupportedModels not found in config');
        }
        
        // Set products from the processed list
        setProducts(productsList);
        setBaseUrl(configData.BaseProductUrl);
        setDesignUrl(configData.DesignUrl);

        if (productsList.length > 0) {
          
          setSelectedProduct(productsList[0]);
          // Handle case differences between Views/views
          const productViews = productsList[0].views || productsList[0].Views || [];
          
          if (productViews.length > 0) {
            setSelectedView(ensureViewObject(productViews[0]));
          }
          // Color selection removed as per requirement
          // Initialize default selected options so UI has immediate state (used by linked_view filtering)
          try {
            const optsCfg = configData?.SupportedModels?.Options || {};
            const defaults = {};
            Object.keys(optsCfg).forEach(k => {
              const vlist = Array.isArray(optsCfg[k]?.values) ? optsCfg[k].values : [];
              if (vlist.length > 0) {
                const first = vlist[0];
                const val = (first && typeof first === 'object') ? first.value : first;
                if (val != null) defaults[k] = { value: val };
              }
            });
            // Merge URL-provided option selections (o1..oN) on top of defaults and set once
            const overrides = getOptionSelectionsFromUrl(optsCfg);
            const merged = overrides && Object.keys(overrides).length > 0
              ? { ...defaults, ...overrides }
              : defaults;
            setGlobalSelectedOptions(merged);
          } catch (e) {
            console.warn('[App] Failed to init default selected options:', e);
          }
        }

        // Check if StickerSet exists and has items
        const stickerSetExists = configData.StickerSet && Array.isArray(configData.StickerSet) && configData.StickerSet.length > 0;
        setHasStickerSet(stickerSetExists);
        
        
        // Check if DesignUrl exists and is not empty
        const hasDesignUrl = configData.DesignUrl && configData.DesignUrl.trim() !== '';
        
        
        // Set the first product as default if available - this is redundant with the above code
        // but keeping it for consistency with the original structure
        if (productsList.length > 0) {
          setSelectedProduct(productsList[0]);
          // Handle case differences between Views/views
          const productViews = productsList[0].views || productsList[0].Views || [];
          
          if (productViews.length > 0) {
            setSelectedView(ensureViewObject(productViews[0]));
          }
          // Color selection removed as per requirement
          // Ensure defaults are set again in case of redundancy
          try {
            const optsCfg = configData?.SupportedModels?.Options || {};
            const defaults = {};
            Object.keys(optsCfg).forEach(k => {
              const vlist = Array.isArray(optsCfg[k]?.values) ? optsCfg[k].values : [];
              if (vlist.length > 0) {
                const first = vlist[0];
                const val = (first && typeof first === 'object') ? first.value : first;
                if (val != null) defaults[k] = { value: val };
              }
            });
            // Re-apply defaults, then URL overrides again to ensure consistency
            const overrides = getOptionSelectionsFromUrl(optsCfg);
            const merged = overrides && Object.keys(overrides).length > 0
              ? { ...defaults, ...overrides }
              : defaults;
            setGlobalSelectedOptions(merged);
          } catch {}
        }
      } catch (error) {
        console.error('Error loading products:', error);
        // Use default products if loading fails
        setProducts(defaultProducts);
        setSelectedProduct(defaultProducts[0]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  const handleProductSelect = (product) => {
    setSelectedProduct(prevProduct => {
      // Only update if product actually changed
      if (!prevProduct || prevProduct.ProductionName !== product.ProductionName) {
        // When product changes, we need to update the selected view to match the new product
        const newProductViews = product.views || product.Views || [];
        
        if (newProductViews.length > 0) {
          // Try to find a matching view in the new product, or use the first view
          const currentViewId = typeof selectedView === 'object' ? selectedView.id : selectedView;
          const matchingView = newProductViews.find(v => {
            const viewId = typeof v === 'object' ? v.id : v;
            return viewId === currentViewId;
          });
          
          // Set the view to the matching view or the first available view
          const newView = matchingView || newProductViews[0];
          
          setSelectedView(ensureViewObject(newView));
        }
        
        return product;
      }
      return prevProduct;
    });
  };
  
  // Add function to set CSS variable for viewport height
  useEffect(() => {
    let baselineVh = Math.round(window.innerHeight || 0);
    let appliedVh = baselineVh;

    const isTextInputFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };

    const getViewportHeight = () => {
      const vv = window.visualViewport;
      if (vv && typeof vv.height === 'number' && isFinite(vv.height)) {
        return Math.round(vv.height);
      }
      return Math.round(window.innerHeight || 0);
    };

    const setAppHeight = (opts = {}) => {
      const { force = false } = opts;
      const vh = getViewportHeight();
      if (!vh) return;

      // Track the largest "normal" height as baseline
      if (vh > baselineVh) baselineVh = vh;

      // When the mobile keyboard opens, visualViewport.height shrinks.
      // On Android this shrink can be small or large depending on device/browser.
      // Avoid shrinking the whole app (and canvas) while an input is focused.
      const focused = isTextInputFocused();
      const shrinking = vh < appliedVh;
      if (!force && focused && shrinking) {
        return;
      }

      appliedVh = vh;
      document.documentElement.style.setProperty('--app-height', `${appliedVh}px`);
    };

    const handleResize = () => setAppHeight();
    const handleOrientationChange = () => setAppHeight({ force: true });
    const handleFocusIn = () => setAppHeight();
    const handleFocusOut = () => setAppHeight({ force: true });
    
    // Set initial height
    setAppHeight({ force: true });
    
    // Update on resize and orientation change
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      if (window.visualViewport && typeof window.visualViewport.removeEventListener === 'function') {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Handle SHOW_THEME_SELECTION event from kiosk attract mode
  useEffect(() => {
    const handleShowThemeSelection = (event) => {
      console.log('[App] SHOW_THEME_SELECTION event received:', event);
      console.log('[App] Showing theme selection from kiosk attract mode');
      
      // Check if kiosk handler exists
      if (window.kioskHandler) {
        console.log('[App] Kiosk handler found, setting theme selection');
        // Clear selected theme to force theme selection to show
        setSelectedTheme(null);
        setKioskThemeSelectionHidden(false);
        setShowThemeSelection(true);
        setShowProductSelection(false);
        
        // Set kiosk step to theme selection
        window.kioskHandler.setKioskStep('theme-selection');
      } else {
        console.warn('[App] Kiosk handler not found!');
      }
    };

    // Handle THEME_SELECTION_CLOSE event from kiosk attract mode
    const handleThemeSelectionClose = (event) => {
      console.log('[App] THEME_SELECTION_CLOSE event received, hiding theme selection');
      setShowThemeSelection(false);
      setKioskThemeSelectionHidden(true);
      setShowProductSelection(false);
    };

    const handleShowProductSelection = (event) => {
      if (!window.kioskHandler) return;
      setShowThemeSelection(false);
      setShowProductSelection(true);
      window.kioskHandler.setKioskStep('product-selection');

      // If we already have a selected product, jump directly to level-2 list for its series
      try {
        const cfg = configDataRef.current;
        const sp = selectedProductRef.current;
        const mv = sp?.Options?.Option1 || sp?.Options?.[resolveOptionKeyByName(cfg?.SupportedModels?.Options || {}, 'model') || 'Option1'];
        const gid = getKioskSeriesIdFromModelValue(mv);
        if (gid) setKioskModelGroup(gid);
      } catch {}
    };

    const handleProductSelectionClose = (event) => {
      setShowProductSelection(false);
    };

    console.log('[App] Setting up SHOW_THEME_SELECTION event listener');
    window.addEventListener('SHOW_THEME_SELECTION', handleShowThemeSelection);
    window.addEventListener('THEME_SELECTION_CLOSE', handleThemeSelectionClose);
    window.addEventListener('SHOW_PRODUCT_SELECTION', handleShowProductSelection);
    window.addEventListener('PRODUCT_SELECTION_CLOSE', handleProductSelectionClose);
    
    return () => {
      console.log('[App] Cleaning up SHOW_THEME_SELECTION event listener');
      window.removeEventListener('SHOW_THEME_SELECTION', handleShowThemeSelection);
      window.removeEventListener('THEME_SELECTION_CLOSE', handleThemeSelectionClose);
      window.removeEventListener('SHOW_PRODUCT_SELECTION', handleShowProductSelection);
      window.removeEventListener('PRODUCT_SELECTION_CLOSE', handleProductSelectionClose);
    };
  }, []);

  // Early route switch to Preview Page (bypass main designer UI)
  const pathname = window.location.pathname || '';
  const search = window.location.search || '';

  // JSON Designer route: use when URL contains 'jsonDesigner'
  const isJsonDesignerRoute = pathname.includes('jsonDesigner') || search.includes('jsonDesigner');
  if (isJsonDesignerRoute) {
    return <JsonDesigner />;
  }

  // Early route switch to Preview Page (bypass main designer UI)
  const isPreviewRoute = pathname.includes('/preview');
  if (isPreviewRoute) {
    return <PreviewPage />;
  }

  if (isLoading) {
    return <div className="app-loading">Loading product...</div>;
  }


  // Defaults: Mobile -> Toolbox; Desktop -> Product
  const effectiveActiveTab = activeTab ?? (isMobile ? 'toolbox' : 'product');
  const showLeftContent = true;
  
  // Detect if we're on mobile (stateful above)
  
  // Show theme selection first if we have config data but no theme selected
  const urlThemeIdx = getThemeIndexFromUrl();
  const hasValidUrlTheme =
    urlThemeIdx !== null &&
    Array.isArray(configData?.Themes) &&
    urlThemeIdx >= 0 &&
    urlThemeIdx < configData.Themes.length;

  const shouldShowThemeSelection = configData && configData.Themes && configData.Themes.length > 0 && !selectedTheme;
  
  // If we should show theme selection, show ONLY that (but skip if a valid URL theme is present)
  const suppressAutoThemeSelection = isKiosk && kioskThemeSelectionHidden && !showThemeSelection;
  if (!suppressAutoThemeSelection && ((shouldShowThemeSelection && !hasValidUrlTheme) || showThemeSelection)) {
    return (
      <div className="app">
        {showClearDesignModal && (
          <div className={`clear-design-modal-backdrop ${showClearDesignModal ? 'show' : ''}`}>
            <div className={`clear-design-modal ${showClearDesignModal ? 'show' : ''}`}>
              <h2>{t('design.clearTitle', 'Clear saved designs?')}</h2>
              <p>{t('design.clearMessage', 'We found saved designs on this device. Do you want to clear them and start fresh?')}</p>
              <div className="clear-design-modal-actions">
                <button onClick={handleKeepDesigns} className="btn-secondary">
                  {t('design.keepButton', 'Keep designs')}
                </button>
                <button onClick={handleClearDesignsConfirm} className="btn-primary">
                  {t('design.clearButton', 'Clear designs')}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Theme Selection Modal - Full Screen Overlay */}
        <ThemeSelection
          themes={configData.Themes || []}
          onThemeSelect={handleThemeSelect}
          onStickerSelect={handleStickerSelect}
          onClose={handleThemeSelectionClose}
          isTestMode={isTestMode}
          selectedThemeIndex={selectedTheme && typeof selectedTheme.index !== 'undefined' ? selectedTheme.index : getThemeIndexFromUrl()}
        />
      </div>
    );
  }

  // Kiosk-only product selection screen
  if (isKiosk && showProductSelection) {
    const optionsCfg = configData?.SupportedModels?.Options || {};
    const modelKey = resolveOptionKeyByName(optionsCfg, 'model') || 'Option1';
    const modelMeta = optionsCfg?.[modelKey];

    const getModelValue = (productObj) => {
      try {
        const raw = productObj?.Options?.[modelKey] ?? productObj?.Options?.Option1;
        return raw ?? '';
      } catch {
        return '';
      }
    };

    const getModelDisplayName = (productObj) => {
      const modelValue = getModelValue(productObj);
      if (!modelValue) return '';
      const values = Array.isArray(modelMeta?.values) ? modelMeta.values : [];
      const found = values.find(v => String(v?.value) === String(modelValue));
      if (found?.displayName) return getOptionDisplayName(found.displayName);
      return String(modelValue);
    };

    const getSeriesInfo = (modelValue) => {
      const raw = String(modelValue || '').trim();
      if (!raw) return { id: '', label: '' };

      // iPhone: IP16 / IP16Pro / IP16PM / IP16Plus etc.
      const ipMatch = raw.match(/^IP\s*(\d{2})/i);
      if (ipMatch) {
        const n = ipMatch[1];
        return { id: `IPHONE_${n}`, label: `iPhone ${n}` };
      }

      // Samsung: S24 / S24Ultra / S23Plus etc.
      const sMatch = raw.match(/^S\s*(\d{2})/i);
      if (sMatch) {
        const n = sMatch[1];
        return { id: `S_${n}`, label: `S${n}` };
      }

      // Fallback: its own group
      return { id: raw, label: raw };
    };

    const getPrettyModelLabel = (modelValue, fallbackDisplayName) => {
      const raw = String(modelValue || '').trim();
      if (!raw) return fallbackDisplayName || '';

      const ip = raw.match(/^IP\s*(\d{2})(.*)$/i);
      if (ip) {
        const n = ip[1];
        const suffix = String(ip[2] || '').trim().replace(/^[-_\s]+/, '');
        const series = `iPhone ${n}`;
        if (!suffix) return series;
        const s = suffix.toLowerCase();
        if (s === 'plus') return `${series} Plus`;
        if (s === 'pro') return `${series} Pro`;
        if (s === 'pm' || s === 'promax' || s === 'pro max') return `${series} Pro Max`;
        // keep unknown suffix as-is
        return `${series} ${suffix}`;
      }

      const sm = raw.match(/^S\s*(\d{2})(.*)$/i);
      if (sm) {
        const n = sm[1];
        const suffix = String(sm[2] || '').trim().replace(/^[-_\s]+/, '');
        const series = `S${n}`;
        if (!suffix) return series;
        const s = suffix.toLowerCase();
        if (s === 'plus') return `${series} Plus`;
        if (s === 'ultra') return `${series} Ultra`;
        return `${series} ${suffix}`;
      }

      return fallbackDisplayName || raw;
    };

    const getPriceTextForModel = (modelValue) => {
      if (!modelValue) return '';
      const variants = Array.isArray(configData?.variants) ? configData.variants : [];
      const prices = variants
        .filter(v => String(v?.[modelKey] ?? v?.Option1 ?? '') === String(modelValue))
        .map(v => v?.price)
        .filter(p => p !== undefined && p !== null && String(p).trim() !== '')
        .map(p => Number(p))
        .filter(n => Number.isFinite(n));
      if (!prices.length) return '';
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      const fmt = (n) => `$${Number(n).toFixed(0)}`;
      if (min === max) return fmt(min);
      return `${fmt(min)}-${fmt(max)}`;
    };

    return (
      <div className="app kiosk-product-selection">
        <div className="kiosk-header">
          <img src="/kiosk/Header.png" alt="Header" className="kiosk-header-bg" />
          <div className="kiosk-header-overlay">
            <div className="kiosk-header-right">
              <img
                src="/kiosk/Back.png"
                alt="Back"
                className="kiosk-back-btn"
                onClick={() => {
                  if (kioskModelGroup) {
                    setKioskModelGroup(null);
                    return;
                  }
                  if (window.kioskHandler) window.kioskHandler.handleKioskBack();
                }}
              />
              <img src="/kiosk/Language.png" alt="Language" className="kiosk-lang-btn" />
              <img
                src="/kiosk/Home.png"
                alt="Home"
                className="kiosk-home-btn"
                onClick={() => {
                  if (window.kioskHandler && typeof window.kioskHandler.enterAttractMode === 'function') {
                    window.kioskHandler.enterAttractMode();
                    return;
                  }
                  window.location.href = '/';
                }}
              />
            </div>
          </div>
        </div>

        <div className="kiosk-product-selection-content">
          <div className="kiosk-product-selection-title">
            {kioskModelGroup
              ? t('kiosk.selectProductModelTitle', 'Select model')
              : t('kiosk.selectProductTitle', 'Please select your product')}
          </div>

          {(() => {
            const list = Array.isArray(products) ? products : [];
            const groupsMap = new Map();
            for (const p of list) {
              const modelValue = getModelValue(p);
              if (!modelValue) continue;
              const series = getSeriesInfo(modelValue);
              const existing = groupsMap.get(series.id) || { id: series.id, label: series.label, items: [] };
              existing.items.push(p);
              groupsMap.set(series.id, existing);
            }

            const groups = Array.from(groupsMap.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));

            if (!kioskModelGroup) {
              return (
                <div className="kiosk-product-grid">
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      className="kiosk-product-card"
                      onClick={() => setKioskModelGroup(g.id)}
                    >
                      <div className="kiosk-product-icon" aria-hidden="true">
                        <img className="kiosk-product-icon-img" src="/kiosk/phone.svg" alt="" />
                      </div>
                      <div className="kiosk-product-name">{g.label}</div>
                    </button>
                  ))}
                </div>
              );
            }

            const groupObj = groupsMap.get(kioskModelGroup);
            const groupItems = Array.isArray(groupObj?.items) ? groupObj.items : [];
            return (
              <div className="kiosk-product-grid">
                {groupItems.map((p, idx) => {
                  const modelValue = getModelValue(p);
                  const disp = getModelDisplayName(p) || p?.DisplayName || p?.name || '';
                  const name = getPrettyModelLabel(modelValue, disp) || p?.ProductionName || `Product ${idx + 1}`;
                  const price = getPriceTextForModel(modelValue);
                  const selected = selectedProduct?.ProductionName && p?.ProductionName
                    ? selectedProduct.ProductionName === p.ProductionName
                    : selectedProduct === p;
                  return (
                    <button
                      key={p?.ProductionName || p?.id || idx}
                      className={`kiosk-product-card${selected ? ' selected' : ''}`}
                      onClick={() => {
                        try {
                          const optsCfg = configData?.SupportedModels?.Options || {};
                          // Build options object dynamically from config order (Option1..OptionN)
                          const orderedKeys = Object.keys(optsCfg)
                            .sort((a, b) => {
                              const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
                              const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
                              return na - nb;
                            });
                          const nextOptions = {};
                          for (const key of orderedKeys) {
                            const raw = p?.Options?.[key];
                            if (raw !== undefined && raw !== null && raw !== '') {
                              nextOptions[key] = { value: raw };
                            }
                          }
                          // Fallback for legacy products where Options might not include the resolved modelKey
                          if (Object.keys(nextOptions).length === 0 && modelValue) {
                            nextOptions[modelKey] = { value: modelValue };
                          }
                          setGlobalSelectedOptions(nextOptions);
                        } catch (e) {
                          console.warn('[kiosk] Failed to set global selected options from product selection:', e);
                        }
                        handleProductSelect(p);
                        setShowProductSelection(false);
                        setKioskModelGroup(null);
                        if (window.kioskHandler) {
                          window.kioskHandler.setKioskStep('designer');
                        }
                      }}
                    >
                      <div className="kiosk-product-icon" aria-hidden="true">
                        <img className="kiosk-product-icon-img" src="/kiosk/phone.svg" alt="" />
                      </div>
                      <div className="kiosk-product-name">{name}</div>
                      {price ? <div className="kiosk-product-price">{price}</div> : null}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }
  
  // Show main app only after theme is selected
  return (
    <div className="app">
      {showClearDesignModal && (
        <div className={`clear-design-modal-backdrop ${showClearDesignModal ? 'show' : ''}`}>
          <div className={`clear-design-modal ${showClearDesignModal ? 'show' : ''}`}>
            <h2>{t('design.clearTitle', 'Clear saved designs?')}</h2>
            <p>{t('design.clearMessage', 'We found saved designs on this device. Do you want to clear them and start fresh?')}</p>
            <div className="clear-design-modal-actions">
              <button onClick={handleKeepDesigns} className="btn-secondary">
                {t('design.keepButton', 'Keep designs')}
              </button>
              <button onClick={handleClearDesignsConfirm} className="btn-primary">
                {t('design.clearButton', 'Clear designs')}
              </button>
            </div>
          </div>
        </div>
      )}
      {!isKiosk && (
      <div className="left-panel">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} showStickersTab={hasStickerSet} configData={configData} />
        
        {/* Render the left content panel only when a tab is active and not in kiosk mode */}
        {showLeftContent && (
          <div className="left-content active">
            <NavbarPanel 
              activeTab={effectiveActiveTab}
              setActiveTab={setActiveTab}
              products={products}
              product={selectedProduct}
              onProductSelect={handleProductSelect}
              konvaController={konvaController}
              canvasContainerRef={canvasContainerRef}
              configData={configData}
              options={configData?.SupportedModels?.Options}
              onShowThemeSelection={handleShowThemeSelection}
              selectedTheme={selectedTheme}
              onThemeSelect={handleThemeSelect}
              onShowLayerModal={handleShowLayerModal}
            />
          </div>
        )}
      </div>
    )}
      {!isKiosk && (
      <div className="right-panel">
        {/* Centralized header for navigation + views */}
        <div className="view-selector">
          {(() => {
            const headerMode = String(configData?.style?.HeaderMode || 'Free').toLowerCase();
            const showViewButtons = headerMode === 'free'; // free = user can pick any view
            return (
              <HeaderBar
                configData={configData}
                selectedProduct={selectedProduct}
                selectedView={selectedView}
                setSelectedView={handleViewSelect}
                onShowThemes={() => handleShowThemeSelection(true)}
                onDone={() => window.dispatchEvent(new CustomEvent('REQUEST_DOWNLOAD_PREVIEW'))}
                showViewButtons={showViewButtons}
              />
            );
          })()}
        </div>
        {selectedProduct && (
          <>
            {/* Keep ViewSelection mounted but hidden for later use */}
            <div style={{ display: 'none' }}>
              <ViewSelection 
                selectedView={selectedView} 
                setSelectedView={handleViewSelect} 
                product={selectedProduct}
                className={''}
                options={configData?.SupportedModels?.Options}
              />
            </div>
            <ProductView 
              ref={canvasContainerRef}
              baseUrl={baseUrl}
              designUrl={designUrl}
              selectedView={selectedView}
              product={selectedProduct}
              onControllerReady={handleControllerReady}
              configData={configData}
              options={configData?.SupportedModels?.Options}
              onShowThemeSelection={handleShowThemeSelection}
              onSyncSelectedView={syncSelectedViewOnly}
            />
          </>
        )}
      </div>
    )}

    {/* In kiosk mode, show custom kiosk UI */}
    {isKiosk && (
      <>
        {/* Kiosk header with Header.png background */}
        <div className="kiosk-header">
          <img src="/kiosk/Header.png" alt="Header" className="kiosk-header-bg" />
          <div className="kiosk-header-overlay">
            <div className="kiosk-header-right">
              <img src="/kiosk/Back.png" alt="Back" className="kiosk-back-btn" onClick={handleKioskBack} />
              <img src="/kiosk/Language.png" alt="Language" className="kiosk-lang-btn" />
              <img
                src="/kiosk/Home.png"
                alt="Home"
                className="kiosk-home-btn"
                onClick={() => {
                  if (window.kioskHandler && typeof window.kioskHandler.enterAttractMode === 'function') {
                    window.kioskHandler.enterAttractMode();
                    return;
                  }
                  window.location.href = '/';
                }}
              />
            </div>
          </div>
        </div>
        
        {/* ProductView directly without wrapper */}
        {selectedProduct && (
          <ProductView 
            ref={canvasContainerRef}
            baseUrl={baseUrl}
            designUrl={designUrl}
            selectedView={selectedView}
            product={selectedProduct}
            onControllerReady={handleControllerReady}
            configData={configData}
            options={configData?.SupportedModels?.Options}
            onShowThemeSelection={handleShowThemeSelection}
            onSyncSelectedView={syncSelectedViewOnly}
          />
        )}
      </>
    )}
    </div>
  );
}

const AppComponent = App;
export default AppComponent;
